import { createTheme, ThemeOptions } from '@mui/material/styles';

// Modern color palette
const colors = {
  primary: {
    50: '#F0F7FF',
    100: '#C2E0FF',
    200: '#99CCF3',
    300: '#66B2FF',
    400: '#3399FF',
    500: '#007FFF',
    600: '#0072E5',
    700: '#0059B2',
    800: '#004C99',
    900: '#003A75',
  },
  grey: {
    50: '#F8F9FA',
    100: '#F3F4F6',
    200: '#E9ECEF',
    300: '#DDE0E4',
    400: '#CED4DA',
    500: '#ADB5BD',
    600: '#6C757D',
    700: '#495057',
    800: '#343A40',
    900: '#212529',
  }
};

const commonComponents = {
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: '6px',
        textTransform: 'none',
        fontWeight: 500,
        padding: '6px 16px',
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 'none',
        },
      },
      contained: {
        '&:hover': {
          backgroundColor: colors.primary[600],
        },
      },
      outlined: {
        borderColor: colors.grey[300],
        '&:hover': {
          backgroundColor: colors.grey[50],
          borderColor: colors.grey[400],
        },
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: '8px',
        border: '1px solid',
        borderColor: colors.grey[200],
        boxShadow: 'none',
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: '8px',
        border: '1px solid',
        borderColor: colors.grey[200],
        boxShadow: 'none',
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: '12px',
        boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
      },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        borderRadius: '8px',
        padding: '8px',
        '&:hover': {
          backgroundColor: colors.grey[100],
        },
      },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        backgroundColor: colors.grey[800],
        borderRadius: '6px',
        fontSize: '0.75rem',
        padding: '6px 12px',
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: '6px',
        fontWeight: 500,
        fontSize: '0.75rem',
      },
      outlined: {
        borderColor: colors.grey[200],
        backgroundColor: 'transparent',
      },
      filled: {
        backgroundColor: colors.grey[100],
      },
    },
  },
};

const commonPalette = {
  primary: {
    main: colors.primary[500],
    light: colors.primary[300],
    dark: colors.primary[700],
    contrastText: '#fff',
  },
  grey: colors.grey,
};

const lightModeWorkstreamColors = {
  FEATURE: colors.primary[500],
  BUGFIX: '#F76707',
  RESEARCH: '#9C36B5',
  DESIGN: '#2F9E44',
  TESTING: '#E8590C',
  DEPLOYMENT: '#495057'
};

const darkModeWorkstreamColors = {
  FEATURE: colors.primary[400],
  BUGFIX: '#FF922B',
  RESEARCH: '#BE4BDB',
  DESIGN: '#51CF66',
  TESTING: '#FF922B',
  DEPLOYMENT: '#868E96'
};

export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    ...commonPalette,
    background: {
      default: colors.grey[50],
      paper: '#ffffff'
    },
    divider: colors.grey[200],
    workstreamColors: lightModeWorkstreamColors,
    text: {
      primary: colors.grey[900],
      secondary: colors.grey[600],
    },
  },
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h4: {
      fontWeight: 600,
      letterSpacing: '-0.02em',
    },
    subtitle1: {
      fontWeight: 500,
    },
    subtitle2: {
      fontWeight: 500,
      color: colors.grey[600],
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: commonComponents,
} as ThemeOptions);

export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    ...commonPalette,
    background: {
      default: '#000000',
      paper: '#111111'
    },
    divider: colors.grey[800],
    workstreamColors: darkModeWorkstreamColors,
    text: {
      primary: colors.grey[100],
      secondary: colors.grey[400],
    },
  },
  typography: lightTheme.typography,
  shape: lightTheme.shape,
  components: {
    ...commonComponents,
    MuiCard: {
      styleOverrides: {
        root: {
          borderColor: colors.grey[800],
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderColor: colors.grey[800],
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: colors.grey[800],
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        outlined: {
          borderColor: colors.grey[700],
        },
        filled: {
          backgroundColor: colors.grey[800],
        },
      },
    },
  },
} as ThemeOptions);

// Add custom palette options
declare module '@mui/material/styles' {
  interface Palette {
    workstreamColors: typeof lightModeWorkstreamColors;
  }
  interface PaletteOptions {
    workstreamColors?: typeof lightModeWorkstreamColors;
  }
}